import { toToneNumbersPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { customType } from "@-/com.npmjs.drizzle-orm/sqlite-core";

const LETTER_FIVE_TONE_SUFFIX_RE = /([A-Za-z])5$/u;
const TONE_DIGIT_SUFFIX_RE = /[0-5]$/u;
const WHITESPACE_SPLIT_RE = /\s+/u;
// `u:` is the CC-CEDICT spelling of ü (`lu:e4`).
// After n/l, `ue` can only be üe (nüè, lüè): standard pinyin has no plain `nue`/`lue` syllable.
const N_L_UE_RE = /^([nNlL])ue(?=[0-5]?$)/u;

/**
 * Pinyin stored as ASCII letters with a tone digit 1–4 at the end of each
 * whitespace-delimited syllable (neutral tone uses 0, not diacritics).
 */
export type ToneNumbersPinyin = string;

function normalizeWhitespaceDelimitedSegment(segment: string): string {
  let s = segment.trim();
  if (s.length === 0) {
    return "";
  }
  s = toToneNumbersPinyin(s);
  // ü is always the ASCII `v` (the PinyinJS adapter already maps `ü`); spell its other forms the same way.
  s = s.replaceAll("u:", "v").replace(N_L_UE_RE, "$1ve");
  // Accept a legacy neutral-tone `5` even when a source is not a recognized syllable.
  s = s.replace(LETTER_FIVE_TONE_SUFFIX_RE, (_, letter: string) => `${letter}0`);
  if (!TONE_DIGIT_SUFFIX_RE.test(s)) {
    s = `${s}0`;
  }
  return s;
}

/** Canonical tone-number form: diacritics → digits; neutral → trailing `0` per syllable. */
export function normalizeToneNumbersPinyin(value: string): ToneNumbersPinyin {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return "";
  }
  return trimmed
    .split(WHITESPACE_SPLIT_RE)
    .map((segment) => normalizeWhitespaceDelimitedSegment(segment))
    .filter((part) => part.length > 0)
    .join(" ");
}

const pinyinTextColumn = customType<{
  data: string;
  driverData: string | null;
}>({
  dataType() {
    return "text";
  },
  fromDriver(value) {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    return normalizeToneNumbersPinyin(value);
  },
  toDriver(value) {
    if (value === null || value === undefined) {
      return null;
    }
    const normalized = normalizeToneNumbersPinyin(value);
    if (normalized.length === 0) {
      return null;
    }
    return normalized;
  },
});

export function toneNumbersPinyinText<Name extends string>(name: Name) {
  return pinyinTextColumn(name);
}
